import { memo, useState, useCallback } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { X, Share2, Plus, Check } from "lucide-react"
import type { PortfolioItem, Artist } from "@/types/models"
import { SaveButton } from "./SaveButton"
import { DeleteConfirmationModal } from "@/components/shared/DeleteConfirmationModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { useAnalytics } from "@/hooks/useAnalytics"
import { CountryFlag } from "@/lib/countryFlags"
import { useImageLoadAndVisible } from "@/hooks/useImageLoadAndVisible"

interface TattooCardProps {
  item: PortfolioItem
  artist?: Artist
  artistsLoading?: boolean
  searchScore?: number
  editMode?: boolean
  onDelete?: (itemId: string) => void
  showCountryFlag?: boolean
  showAddToCollection?: boolean
  onAddToCollection?: (itemId: string) => Promise<void>
  analyticsSource?: string
  feedType?: string
  onImageLoad?: () => void
  preloaded?: boolean
  selectionMode?: boolean
  isSelected?: boolean
  canShowSelectionIndicator?: boolean
  onToggleSelect?: () => void
  onDetailClick?: (itemId: string) => void
  /** When true, only the save/add action is shown (matches FlashCard, no share chip). */
  hideShare?: boolean
}

export const TattooCard = memo(function TattooCard({ 
  item, 
  artist, 
  artistsLoading = false,
  searchScore,
  editMode = false,
  onDelete,
  showCountryFlag = false,
  showAddToCollection = false,
  onAddToCollection,
  analyticsSource,
  feedType,
  onImageLoad,
  preloaded = false,
  selectionMode = false,
  isSelected = false,
  canShowSelectionIndicator = true,
  onToggleSelect,
  onDetailClick,
  hideShare = false
}: TattooCardProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const [showShareModal, setShowShareModal] = useState(false)
  const [addingToCollection, setAddingToCollection] = useState(false)

  // Use the reusable hook for image load and visibility detection
  const { imageVisible, imgRef, handleImageLoad, handleImageError } = useImageLoadAndVisible({
    onImageLoadAndVisible: onImageLoad,
    preloaded
  })

  const handleAddToCollectionClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!onAddToCollection || addingToCollection) return
    
    setAddingToCollection(true)
    try {
      await onAddToCollection(item.id)
      // Keep the check icon visible for a moment to show success
      setTimeout(() => {
        setAddingToCollection(false)
      }, 1500)
    } catch (error) {
      console.error('Error adding to collection:', error)
      setAddingToCollection(false)
    }
  }, [onAddToCollection, item.id, addingToCollection])

  const isDiscoverFeedRoute =
    location.pathname === '/feed' || location.pathname === '/discover'

  const resolvedSource = analyticsSource || (
    isDiscoverFeedRoute
      ? 'feed'
      : location.pathname.startsWith('/artist/')
      ? 'artist_page'
      : location.pathname.startsWith('/profile/collection')
      ? 'collection'
      : 'unknown'
  )

  const handleCardClick = useCallback(() => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect()
      return
    }
    if (!editMode) {
      trackEvent('item_clicked', {
        item_id: item.id,
        item_type: item.isHealed ? 'healed' : 'tattoo',
        artist_id: item.artistId,
        source: resolvedSource,
        feed_type: feedType
      })
      
      // If a detail click handler is provided, use it instead of navigating
      if (onDetailClick) {
        onDetailClick(item.id)
        return
      }
      
      if (isDiscoverFeedRoute) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('tattoo', item.id)
        setSearchParams(newParams)
      } else {
        // Navigate to healed detail page if it's a healed item, otherwise to tattoo detail page
        const route = item.isHealed ? `/healed/${item.id}` : `/tattoos/${item.id}`
        if (location.pathname.startsWith('/profile/collection')) {
          navigate(route, {
            state: {
              from: {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash
              },
              scrollPosition: window.scrollY
            }
          })
        } else {
          navigate(route)
        }
      }
    }
  }, [editMode, feedType, item.artistId, item.id, item.isHealed, location.hash, location.pathname, location.search, navigate, resolvedSource, searchParams, setSearchParams, trackEvent, selectionMode, onToggleSelect, onDetailClick])

  const handleArtistClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click
    if (artist?.id && !editMode) {
      navigate(`/artist/${artist.id}`)
    }
  }, [navigate, artist?.id, editMode])

  const handleDelete = useCallback(() => {
    onDelete?.(item.id)
  }, [onDelete, item.id])
  
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
  }

  const handleShare = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const currentUrl = `${window.location.origin}/tattoos/${item.id}`
    
    if (isMobileDevice() && navigator.share) {
      navigator.share({
        title: `Tattoo by ${artist?.displayName || 'Artist'}`,
        text: `Check out this tattoo!`,
        url: currentUrl,
      })
        .then(() => {
          trackEvent('item_shared', {
            item_id: item.id,
            item_type: item.isHealed ? 'healed' : 'tattoo',
            artist_id: item.artistId,
            source: resolvedSource,
            share_method: 'native'
          })
        })
        .catch(console.error)
    } else {
      setShowShareModal(true)
    }
  }, [artist, item.artistId, item.id, item.isHealed, resolvedSource, trackEvent])

  return (
    <div className="flex flex-col">
      <div 
        className={`relative w-full group ${editMode ? 'cursor-default' : 'cursor-pointer'}`}
        onClick={handleCardClick}
        style={item.width && item.height ? { aspectRatio: `${item.width}/${item.height}` } : undefined}
      >
          {/* Skeleton stays visible until image is fully visible */}
          <div className={`absolute inset-0 w-full bg-muted rounded-[var(--card-radius)] transition-opacity duration-300 ${
            preloaded || imageVisible ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-pulse'
          }`} />
          
          <img
            ref={imgRef}
            src={item.compressedImageUrl || item.imageUrl}
            alt={item.caption || "Tattoo design"}
            className={`relative w-full h-auto object-cover rounded-[var(--card-radius)] block shadow-lg transition-opacity duration-500 ease-out ${
              preloaded || imageVisible ? 'opacity-100' : 'opacity-0'
            }`}
            loading="eager"
            onLoad={preloaded ? undefined : handleImageLoad}
            onError={preloaded ? undefined : handleImageError}
          />
          {/* Primary color overlay */}
          <div className="absolute inset-0 bg-primary/5 rounded-[var(--card-radius)]" />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-[var(--card-radius)]" />
          
          {/* Dev mode: Search score overlay */}
          {import.meta.env.DEV && searchScore !== undefined && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <span className="text-red-600 font-bold text-sm bg-white/90 px-2 py-1 rounded shadow-lg">
                {searchScore.toFixed(3)}
              </span>
            </div>
          )}
          
          {/* Delete button - Top Right (Edit Mode) */}
          {editMode && onDelete && (
            <DeleteConfirmationModal
              item={item}
              type="tattoo"
              onConfirm={handleDelete}
            >
              <div 
                className="absolute top-2 right-2 bg-background text-foreground w-7 h-7 rounded-full flex items-center justify-center cursor-pointer hover:bg-background/80 transition-all duration-300 shadow-lg animate-in fade-in-0 zoom-in-50 duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </div>
            </DeleteConfirmationModal>
          )}
          
          {/* Selection circle - Bottom Right (Selection Mode) */}
          {selectionMode && canShowSelectionIndicator && (
            <div 
              className="absolute bottom-2 right-2"
              onClick={(e) => {
                e.stopPropagation()
                onToggleSelect?.()
              }}
            >
              <div 
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all shadow-sm cursor-pointer ${
                  isSelected 
                    ? 'bg-primary border-primary' 
                    : 'bg-background/90 border-foreground/30'
                }`}
              >
                {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
              </div>
            </div>
          )}

          {/* Share + Save pill (or save-only when hideShare) — Bottom Right; hidden in edit/selection */}
          {!editMode && !selectionMode && (
            <div className="absolute bottom-2 right-2" onClick={(e) => e.stopPropagation()}>
              <div className="bg-primary rounded-full flex items-center overflow-hidden shadow-sm">
                {!hideShare && (
                  <>
                    <button 
                      type="button"
                      onClick={handleShare}
                      className="text-primary-foreground text-xs font-medium px-2 py-1 flex items-center gap-1 transition-colors hover:bg-primary/80"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <div className="h-4 w-px bg-primary-foreground/20" />
                  </>
                )}
                {showAddToCollection ? (
                  <button
                    onClick={handleAddToCollectionClick}
                    disabled={addingToCollection}
                    className="text-primary-foreground text-xs font-medium px-2 py-1 flex items-center gap-1 transition-colors hover:bg-primary/80 disabled:opacity-50"
                  >
                    {addingToCollection ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </button>
                ) : (
                  <SaveButton
                    item={item}
                    className="hover:bg-primary/80 text-primary-foreground"
                    source={resolvedSource}
                  />
                )}
              </div>
            </div>
          )}

          {/* Country Flag - Bottom Left */}
          {showCountryFlag && !editMode && !selectionMode && (
            <div className="absolute bottom-2 left-2">
              <CountryFlag country={artist?.location?.country} showBorder={false} customSizeInPx={20} />
            </div>
          )}
        </div>
      
      {/* Artist info */}
      {(artist || artistsLoading) && (
        <div className="flex flex-col gap-2 card-content px-2 bg-background">
          <div className="flex justify-center items-center">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-opacity"
              onClick={handleArtistClick}
            >
              {artist?.avatarUrl && (
                <img
                  src={artist.avatarUrl}
                  alt={artist.displayName}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
              )}
              {artistsLoading && !artist && (
                <div className="h-6 w-6 bg-muted rounded-full animate-pulse" />
              )}
              <span className="text-card-artist truncate">
                {artistsLoading && !artist ? (
                  <div className="h-4 bg-muted rounded animate-pulse w-20" />
                ) : (
                  artist?.displayName || t('common.artistName')
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {!hideShare && (
        <ShareModal 
          isOpen={showShareModal}
          onOpenChange={setShowShareModal}
          url={`${window.location.origin}${item.isHealed ? '/healed' : '/tattoos'}/${item.id}`}
          title={`${item.isHealed ? 'Healed tattoo' : 'Tattoo'} by ${artist?.displayName || 'Artist'}`}
          description={item.isHealed ? "Check out this healed tattoo!" : "Check out this tattoo!"}
          imageUrl={item.imageUrl}
          itemId={item.id}
          itemType={item.isHealed ? 'healed' : 'tattoo'}
          artistId={item.artistId}
          source={resolvedSource}
        />
      )}
    </div>
  )
})
